import React from "react";
import { CreditCard, MapPin, Trash2 } from "lucide-react";
import ParagraphTag from "./Paragraph";

const CardDisplay = ({ cards, onAddCard, onDeleteCard, isDeleting }) => {
  const getBrandIcon = (brand) => {
    const brandColors = {
      VISA: "text-blue-600",
      MASTERCARD: "text-red-600",
      AMEX: "text-green-600",
      DISCOVER: "text-orange-600",
    };
    return brandColors[brand] || "text-gray-600";
  };

  const getBrandBg = (brand) => {
    const brandBgs = {
      VISA: "bg-blue-50 border-blue-200",
      MASTERCARD: "bg-red-50 border-red-200",
      AMEX: "bg-green-50 border-green-200",
      DISCOVER: "bg-orange-50 border-orange-200",
    };
    return brandBgs[brand] || "bg-gray-50 border-gray-200";
  };

  if (!cards || cards.length === 0) {
    return (
      <div className="mt-4">
        {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CreditCard className="w-5 h-5 text-gray-400 mr-2" />
              <ParagraphTag
                content="No payment card added yet"
                classes="text-gray-600 font-popinsMedium text-sm"
              />
            </div>
            <button
              type="button"
              className="bg-themeColor text-white px-4 py-2 rounded-md text-sm font-popinsSemiBold hover:bg-themeColor/90 transition-colors"
              onClick={onAddCard}
            >
              Add a Card
            </button>
          </div>
        </div> */}
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="mb-3">
        <ParagraphTag
          content="Payment Card"
          classes="text-darkColor font-popinsSemiBold text-base"
        />
      </div>

      <div className="space-y-3">
        {cards.map((card, index) => (
          <div
            key={card.id || index}
            className={`${getBrandBg(
              card.brand
            )} border rounded-lg p-4 transition-all hover:shadow-md`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {/* Card Header */}
                <div className="flex items-center mb-3">
                  <CreditCard
                    className={`w-6 h-6 ${getBrandIcon(card.brand)} mr-2`}
                  />
                  <ParagraphTag
                    content={card.brand}
                    classes={`${getBrandIcon(
                      card.brand
                    )} font-popinsBold text-sm`}
                  />
                  {card.status && (
                    <span
                      className={`ml-3 px-2 py-1 text-xs font-popinsSemiBold rounded ${
                        card.status === "ACTIVE"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {card.status}
                    </span>
                  )}
                </div>

                {/* Card Details */}
                <div className="space-y-2">
                  <div className="flex items-center">
                    <ParagraphTag
                      content="Card Number:"
                      classes="text-gray-600 font-popinsMedium text-xs mr-2"
                    />
                    <ParagraphTag
                      content={`•••• •••• •••• ${card.cardNumberLast4}`}
                      classes="text-darkColor font-popinsSemiBold text-sm"
                    />
                  </div>

                  <div className="flex items-center">
                    <ParagraphTag
                      content="Card Holder:"
                      classes="text-gray-600 font-popinsMedium text-xs mr-2"
                    />
                    <ParagraphTag
                      content={card.holderName}
                      classes="text-darkColor font-popinsMedium text-sm"
                    />
                  </div>

                  <div className="flex items-center">
                    <ParagraphTag
                      content="Expires:"
                      classes="text-gray-600 font-popinsMedium text-xs mr-2"
                    />
                    <ParagraphTag
                      content={`${String(card.expiryMonth).padStart(
                        2,
                        "0"
                      )}/${card.expiryYear}`}
                      classes="text-darkColor font-popinsMedium text-sm"
                    />
                  </div>

                  {card.cardType && (
                    <div className="flex items-center">
                      <ParagraphTag
                        content="Type:"
                        classes="text-gray-600 font-popinsMedium text-xs mr-2"
                      />
                      <ParagraphTag
                        content={card.cardType}
                        classes="text-darkColor font-popinsMedium text-sm"
                      />
                    </div>
                  )}

                  {/* Billing Address */}
                  {card.billingAddress && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-start">
                        <MapPin className="w-4 h-4 text-gray-500 mr-2 mt-0.5" />
                        <div>
                          <ParagraphTag
                            content="Billing Address:"
                            classes="text-gray-600 font-popinsMedium text-xs mb-1"
                          />
                          <ParagraphTag
                            content={`${card.billingAddress.addressLine1}, ${card.billingAddress.city}, ${card.billingAddress.state} ${card.billingAddress.zip}`}
                            classes="text-darkColor font-popinsRegular text-xs"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Created/Updated dates */}
                  {(card.createdOn || card.lastUpdatedOn) && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      {card.createdOn && (
                        <ParagraphTag
                          content={`Added: ${new Date(
                            card.createdOn
                          ).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })}`}
                          classes="text-gray-500 font-popinsRegular text-xs"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Delete Button */}
              {onDeleteCard && (
                <button
                  type="button"
                  onClick={() => onDeleteCard(card.id)}
                  disabled={isDeleting}
                  className="ml-4 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Delete card"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CardDisplay;
